import React from 'react';
import PropTypes from 'prop-types';
import Label from './Label';
import Value from './Value';
import styled from 'styled-components';
import widthMixin from '../../styles/utils/widthMixin';
import Icon from '@mdi/react';
import { DefaultPill } from '../StatusPill';
import DeprecatedTooltip from './Tooltip';
import TriggerPopover from './TriggerPopover';
import { useId } from '@fluentui/react-hooks';
import { TooltipHost } from '@fluentui/react/lib/Tooltip';

const OverviewFieldContainer = styled.div`
  display: flex;
  flex: 0 1 auto;
  flex-direction: row;
  padding: 2px 0;
`;

const LabelBlock = styled.div`
  ${widthMixin}
  display: inline-block;
`;

const ValueBlock = styled.div`
  ${widthMixin}
  display: inline-block;
`;

const LabelInner = styled.div<{ labelWidth: number }>`
  display: inline-block;
  min-width: ${(props) => (props.labelWidth ? `${props.labelWidth}px` : 'inherit')};
`;

const InlineFieldHintIcon = ({ path, color, hint }: { path: string; color: string; hint: string }) => {
  const inlineFieldHintId = useId(path);

  return (
    <TooltipHost content={hint} id={inlineFieldHintId}>
      <Icon
        path={path}
        color={color}
        size={0.75}
        style={{ verticalAlign: 'bottom', paddingLeft: '3px' }}
        aria-describedby={inlineFieldHintId}
      />
    </TooltipHost>
  );
};

const OverviewInlineField = (props: any) => {
  const {
    alignLabel,
    alignValue,
    bold,
    id,
    label,
    includeLabel,
    flexBox,
    labelCols,
    labelAddon,
    inputCols,
    view,
    uppercaseLabel,
    verticalAlign,
    style,
    icon,
    labelWidth,
    hint, //Note - 'icon' also required to use hint with this field.
  } = props;

  const labelProps = {
    bold,
    flexBox,
    id,
    includeLabel,
    label,
    labelAddon,
    view,
    alignLabel,
    uppercaseLabel,
    verticalAlign,
    labelWidth,
  };

  return (
    <OverviewFieldContainer style={style}>
      {label ? (
        <LabelBlock cols={labelCols}>
          <Label {...labelProps} view={view} bold={bold}>
            <LabelInner labelWidth={labelWidth}>{label}</LabelInner>
            {icon ? <InlineFieldHintIcon path={icon.path} color={icon.color} hint={hint} /> : null}
            {labelAddon ? (
              // @ts-ignore
              <DefaultPill small inline style={{ marginLeft: '0.5em' }}>
                {labelAddon}
              </DefaultPill>
            ) : null}
          </Label>
        </LabelBlock>
      ) : null}
      <ValueBlock cols={inputCols}>
        <Value {...props} align={alignValue} />
      </ValueBlock>
    </OverviewFieldContainer>
  );
};

OverviewInlineField.propTypes = {
  includeLabel: PropTypes.bool,
  labelCols: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  inputCols: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  id: PropTypes.string,
  iconSuffix: PropTypes.bool,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  label: PropTypes.string,
  alignLabel: PropTypes.string,
  alignValue: PropTypes.string,
  uppercaseLabel: PropTypes.string,
  view: PropTypes.bool,
  fontSize: PropTypes.string,
  align: PropTypes.string,
  labelDisplay: PropTypes.string,
  verticalAlign: PropTypes.string,
  initialCaps: PropTypes.bool,
  labelWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  decimals: PropTypes.number, //Pass-through for Value
  style: PropTypes.any,
  type: PropTypes.string, //Pass-through
  icon: PropTypes.any,
  hint: PropTypes.string,
};
OverviewInlineField.defaultProps = {
  includeLabel: true,
  inputPrefix: null,
  flexBox: true,
  labelCols: 1 / 2,
  inputCols: 1 / 2,
  value: '',
  label: '',
  id: '',
  iconSuffix: false,
  bold: false,
  view: true,
  alignLabel: 'left',
  alignValue: 'right',
  uppercaseLabel: 'none',
  fontSize: '14px',
  align: 'right',
  labelDisplay: 'block',
  verticalAlign: 'top',
  initialCaps: false,
};

export default OverviewInlineField;
